import type { IApiBatter, IApiCatcher, IApiPitcher, IApiPlayerExtended } from '@trackman/baseball-shared-components';
import { CallTypes, HitTypes, KOrBB, PitchTypes, constants } from '@trackman/baseball-shared-components';
import { IApiBallFinal } from 'interfaces/broadcast/IApiBallFinal';
import type { IChallengeTag } from 'interfaces/tagging/ApiModels/IChallengeTag';
import type { ITag } from 'interfaces/tagging/ITag';
import { action, computed, makeObservable, observable } from 'mobx';
import { IPlay } from 'models/tagging/IPlay';
import { custom, object, serializable } from 'serializr';
import { TaggingFlowStateMachine } from 'services/helper-services/TaggingFlowStateMachine';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { v1 as uuid } from 'uuid';
import { IApiSessionMetadataUpdated } from '../../interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import type { IStrikeZoneDecision } from '../../interfaces/tagging/ApiModels/IStrikeZoneDecision';
import { isHomeTeamBatting } from '../../services/helper-services/baseballLogicHelpers';
import type { BaseRunners } from '../baseRunners/BaseRunners';
import { AdjustedValue } from './AdjustedValue';
import { PlayLineup } from './PlayLineup';

const simpleSerialize = (item: any) => JSON.stringify(item);
const simpleDeserialize = (json: any) => JSON.parse(json);

export class Play implements IPlay {
  constructor(playId: string, ballFinal?: IApiBallFinal) {
    this.id = uuid();
    this.playId = playId;
    this.balls = new AdjustedValue();
    this.strikes = new AdjustedValue();
    this.outs = new AdjustedValue();
    this.homeTeamScore = new AdjustedValue();
    this.awayTeamScore = new AdjustedValue();
    this.tag = {
      runs: 0,
      outs: 0,
      catcherThrows: undefined,
      hitType: HitTypes.Undefined,
      pitchType: PitchTypes.Undefined,
      pitchCall: CallTypes.Undefined,
    } as ITag;
    this.isChanged = false;
    this.isWarmup = false;
    this.isDeleted = false;
    this.pitcher = { ...constants.emptyPlayer };
    this.batter = { ...constants.emptyPlayer };
    this.catcher = { ...constants.emptyPlayer };
    this.firstBaseman = null;
    this.secondBaseman = null;
    this.thirdBaseman = null;
    this.centerFielder = null;
    this.leftFielder = null;
    this.rightFielder = null;
    this.shortStopper = null;
    this.sequenceNumber = 0;
    this.playSequenceNumber = undefined;
    this.note = '';
    this.isNoteResolved = false;
    this.korbb = KOrBB.Undefined;
    this.isPlaceholder = false;
    this.trackStartTimeUtc = undefined;
    this.trackStartTimeAutoGenerated = false;
    this.playLineup = new PlayLineup([], []);
    this.ballFinals = ballFinal ? [ballFinal] : [];
    this.flowStateMachine = new TaggingFlowStateMachine();
    this.fieldingSequence = [null, null, null];
    this.baseRunners = {
      firstBase: null,
      secondBase: null,
      thirdBase: null,
    };
    this.isBRChangedManually = false;
    this.isBaseRunnersLogicExecuted = false;
    this.isFieldingSequenceChanged = false;
    makeObservable(this);
  }

  @serializable
  @observable
  public id: string;

  @serializable
  @observable
  public playId?: string;

  @observable trackStartTimeAutoGenerated: boolean;

  @observable isFieldingSequenceChanged: boolean;

  @observable isCatcherThrowsSaved?: boolean;

  @observable fieldingSequence: any;

  @observable isBRChangedManually: boolean;

  @observable pitcher: IApiPitcher;

  @observable batter: IApiBatter;

  @observable catcher: IApiCatcher;

  @observable firstBaseman: IApiPlayerExtended;

  @observable secondBaseman: IApiPlayerExtended;

  @observable thirdBaseman: IApiPlayerExtended;

  @observable centerFielder: IApiPlayerExtended;

  @observable leftFielder: IApiPlayerExtended;

  @observable rightFielder: IApiPlayerExtended;

  @observable shortStopper: IApiPlayerExtended;

  @observable baseRunners: BaseRunners;

  @observable isBaseRunnersLogicExecuted: boolean;

  @serializable(object(AdjustedValue))
  @observable
  balls: AdjustedValue;

  @serializable(object(AdjustedValue))
  @observable
  strikes: AdjustedValue;

  @serializable(object(AdjustedValue))
  @observable
  outs: AdjustedValue;

  @serializable
  @observable
  isCreatedManually = false;

  @serializable
  @observable
  isTagStored = false;

  @serializable
  @observable
  isValidated = false;

  @serializable(custom(simpleSerialize, simpleDeserialize))
  @observable
  tag: ITag;

  @serializable(object(AdjustedValue))
  @observable
  homeTeamScore: AdjustedValue;

  @serializable(object(AdjustedValue))
  @observable
  awayTeamScore: AdjustedValue;

  @serializable
  @observable
  inningNumber = 1;

  @serializable
  @observable
  isTopInning = true;

  @observable
  isChanged: boolean;

  @serializable
  @observable
  isWarmup: boolean;

  @serializable
  @observable
  isDeleted: boolean;

  @serializable
  @observable
  sequenceNumber: number;

  @serializable
  @observable
  playSequenceNumber?: number;

  @serializable
  @observable
  note: string;

  @serializable
  @observable
  isNoteResolved: boolean;

  @serializable
  @observable
  korbb: KOrBB;

  pitchBallFinalTrackId?: string;

  hitBallFinalTrackId?: string;

  throwBallFinalTrackId?: string;

  @serializable
  @observable
  pAofInning?: number;

  @serializable
  @observable
  pitchOfPA?: number;

  @serializable
  @observable
  isPlaceholder: boolean;

  @serializable
  @observable
  trackStartTimeUtc?: string;

  @observable
  playLineup: PlayLineup;

  @observable
  shouldAnimate = false;

  @observable
  ballFinals: IApiBallFinal[];

  @observable
  strikeZoneDecision?: IStrikeZoneDecision;

  @observable
  challengeTag?: IChallengeTag;

  flowStateMachine: TaggingFlowStateMachine;

  @action saveCatcherThrow = (value: boolean) => {
    this.isCatcherThrowsSaved = value;
  };

  @action addBallFinal = (ballFinal: IApiBallFinal) => {
    if (this.ballFinals.find((bf) => bf.playId === ballFinal.playId && bf.kind === ballFinal.kind)) {
      return; // this BallFinal already exists, probably got redelivered
    }
    this.ballFinals = [...this.ballFinals, ballFinal];
  };

  @action clearInningRelatedData = () => {
    this.inningNumber = 1;
    this.isTopInning = true;
    this.pitcher = { ...constants.emptyPlayer };
    this.batter = { ...constants.emptyPlayer };
    this.catcher = { ...constants.emptyPlayer };
  };

  @action setOuts = (outs: number) => {
    this.tag.outs = outs;
    this.outs.fromTag = outs;
  };

  @action setRuns = (runs: number) => {
    this.tag.runs = runs;
    if (this.isTopInning) {
      this.awayTeamScore.fromTag = runs;
    } else {
      this.homeTeamScore.fromTag = runs;
    }
  };

  @computed get isLive() {
    return !this.isWarmup && !this.isDeleted;
  }

  getPitchingTeamLineup = () => {
    if (this.isTopInning) {
      return this.playLineup.homeTeam;
    }
    return this.playLineup.awayTeam;
  };

  getBattingTeamLineup = () => {
    if (this.isTopInning) {
      return this.playLineup.awayTeam;
    }
    return this.playLineup.homeTeam;
  };

  @computed get isHomeTeamBatting(): boolean {
    return isHomeTeamBatting(this.isTopInning);
  }

  getPitchingTeam = (metadata: IApiSessionMetadataUpdated) => {
    return this.isHomeTeamBatting ? metadata.awayTeam : metadata.homeTeam;
  };

  getBattingTeam = (metadata: IApiSessionMetadataUpdated) => {
    return this.isHomeTeamBatting ? metadata.homeTeam : metadata.awayTeam;
  };

  toApiModel = (metadata: IApiSessionMetadataUpdated) => {
    return mappingHelpers.playToApiPlayModelMap(this, metadata);
  };

  @computed get canBeSaved(): boolean {
    return !this.isPlaceholder && this.isChanged && this.tag.pitchCall !== CallTypes.Undefined;
  }

  @computed get isFieldingSequenceCompleted(): boolean {
    return (
      this.fieldingSequence.length > 1 &&
      this.fieldingSequence[0] !== null &&
      this.fieldingSequence[1] !== null &&
      this.fieldingSequence[2] !== null
    );
  }

  @computed get isBaseRunnersCompleted(): boolean {
    return (
      (this.baseRunners?.firstBase !== undefined ||
        this.baseRunners?.secondBase !== undefined ||
        this.baseRunners?.thirdBase !== undefined) &&
      (this.baseRunners?.firstBase !== null || this.baseRunners?.secondBase !== null || this.baseRunners?.thirdBase !== null)
    );
  }
}
